import { WALLET_LABELS } from "@nexus-cross/crossx-design-system";
import type { CSSProperties } from "react";

/** CAIP-2 chain identifier (e.g. "eip155:612044") */
export type ChainId = `eip155:${number}`;

export enum ConnectorId {
  CROSSx = "crossx",
  MetaMask = "io.metamask",
  Binance = "com.binance.wallet",
}

export interface ConnectorMeta {
  name: string;
  iconUrl: string;
}

export const CONNECTOR_REGISTRY: Record<ConnectorId, ConnectorMeta> = {
  [ConnectorId.CROSSx]: {
    name: WALLET_LABELS.default,
    iconUrl: "https://contents.crosstoken.io/wallet/token/images/tCROSS.svg",
  },
  [ConnectorId.MetaMask]: {
    name: "MetaMask",
    iconUrl: "https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg",
  },
  [ConnectorId.Binance]: {
    name: "Binance Wallet",
    iconUrl: "https://public.bnbstatic.com/static/images/common/favicon.ico",
  },
};

export interface PreferredToken {
  chainId: ChainId;
  address: string;
}

export interface WalletInfoStyle extends CSSProperties {
  "--wi-primary"?: string;
  "--wi-secondary"?: string;
  "--wi-surface-bg"?: string;
  "--wi-surface-default"?: string;
  "--wi-surface-subtle"?: string;
  "--wi-border-default"?: string;
  "--wi-border-subtle"?: string;
  "--wi-texticon-primary"?: string;
  "--wi-texticon-secondary"?: string;
  "--wi-texticon-tertiary"?: string;
}

export interface TokenBalance {
  blockNumber: number;
  name: string;
  symbol: string;
  chainId: number;
  address: string;
  quantity: {
    decimals: number;
    numeric: string;
  };
  icon_url: string;
}

export interface TokenBalanceResponse {
  code: number;
  message: string;
  data: TokenBalance[];
}

export interface TokenStats {
  chain_id: number;
  address: string;
  price: string;
  percent_change_24h: string;
  /** 시장 유통량. `/v1/public/token/stats`가 제공(문자열). 상세화면 Total Supply 표기용. */
  circulating_supply?: string;
  market_cap?: string;
  volume_24h?: string;
}

export interface TokenStatsResponse {
  code: number;
  message: string;
  data: TokenStats[];
}

export interface TokenInfo {
  chain_id: number;
  address: string;
  category: string;
  /**
   * `/v1/public/token/info` 카탈로그에 함께 담겨 오는 메타데이터. 토큰 상세화면의
   * Social Media 링크 및 컨트랙트 배포 여부 판별에 사용한다. 응답에 없을 수 있어
   * 모두 optional로 둔다.
   */
  name?: string;
  symbol?: string;
  decimals?: number;
  image?: string;
  deployed?: boolean;
  info_url?: string | null;
  x_url?: string | null;
  telegram_url?: string | null;
  medium_url?: string | null;
  discord_url?: string | null;
}

export interface TokenInfoResponse {
  code: number;
  message: string;
  data: TokenInfo[];
}

/** `/v1/public/token/historical` 단건. Receive/Send는 from/to를 지갑주소와 비교해 판별한다. */
export interface TokenHistoryItem {
  amount: string;
  block_number: number;
  chain_id: number;
  from: string;
  from_alias: string;
  log_index: number;
  method: string;
  method_alias: string;
  nonce: number;
  removed: boolean;
  timestamp: number;
  to: string;
  to_alias: string;
  token: string;
  tx: string;
  tx_index: number;
  gas_price: number;
  gas_used: number;
  max_fee_per_gas: number;
  status: "success" | "fail";
}

export interface TokenHistoryResponse {
  code: number;
  message: string;
  data: TokenHistoryItem[];
}

/** `/v1/public/token/price/historical`가 받는 interval 값(표시 라벨과 다름). */
export type TokenPriceInterval = "10m" | "1h" | "1d" | "7d" | "30d" | "1y";

/** 가격 히스토리 단건. `updated_at`은 unix 초. */
export interface TokenPricePoint {
  price: string;
  updated_at: number;
}

export interface TokenPriceHistoryResponse {
  code: number;
  message: string;
  data: TokenPricePoint[];
}

export interface ForgeHolding {
  token: string;
  token_name: string;
  token_symbol: string;
  balance: string;
  current_price: string;
  value: string;
  average_cost_usd: number;
  current_value_usd: number;
  unrealized_pnl_usd: number;
  unrealized_pnl_cross: number;
  pnl_percentage: number;
}

export interface ForgeHoldingsResponse {
  success: boolean;
  data: {
    items: ForgeHolding[];
  };
}

export interface ForgeTokenDetail {
  address: string;
  name: string;
  symbol: string;
  image_url: string;
}

export interface ForgeTokenDetailResponse {
  success: boolean;
  data: {
    token: ForgeTokenDetail;
  };
}
